export const RECENT_SEARCHES_KEY = 'recent-searches'

export const visualOptions = [
  {
    label: 'Household Concentration',
    value: 'householdConcentration'
  },
  {
    label: 'Individual Households',
    value: 'individualHouseholds'
  }
]

export const FilterCategoryIndex = {
  GEOGRAPHIC_INDICATORS: 4,
  CLIMATE_INDICATORS: 6
}

export const enrolledOptions = [
  {
    label: 'All',
    value: 2
  },
  {
    label: 'Yes',
    value: 1
  },
  {
    label: 'No',
    value: 0
  }
]

export const dimensions = {
  state: 'State',
  county: 'County',
  congressionalDistrict: 'Cong. Dist',
  censusTract: 'Cen. Tract',
  population: 'Selected Adult Population',
  count: 'Selected Unique Addresses'
}

export const isNotEmpty = (data) => {
  return data !== null && data !== undefined && Array.isArray(data) && data.length > 0
}

const populateLegendText = (isConcentrationView, section, type) => {
  const enrolledText = ' Enrolled'
  const notEnrolledText = ' Not Enrolled'
  const concentrationText = 'Concentration of '
  if (type === 'enrolled') {
    return isConcentrationView ? concentrationText + section + enrolledText : section + enrolledText
  } else {
    return isConcentrationView ? concentrationText + section + notEnrolledText : section + notEnrolledText
  }
}

/**
 * Get outreach simulation legends using new filter management data
 * @param {string} activeOutreachType - 'benefits' | 'workforce' | null (from useOutreachFilters)
 * @param {number} enrolledValue - 0 | 1 | 2 (from useOutreachFilters)
 * @param {string} selectedFocusLayer - Current focus layer value
 * @returns {Array} Array of legend objects with type and legendText
 */
export const getOutreachLegends = (activeOutreachType, enrolledValue, selectedFocusLayer) => {
  if (!activeOutreachType) return []

  const legends = []
  const enrolledType = 'enrolled'
  const notEnrolledType = 'not-enrolled'
  const isConcentrationView = selectedFocusLayer === visualOptions[0].value
  const section = activeOutreachType === 'benefits' ? 'Benefits' : 'Recruitment'

  if (enrolledValue === 2) {
    legends.push(
      {
        type: enrolledType,
        legendText: populateLegendText(isConcentrationView, section, enrolledType)
      },
      {
        type: notEnrolledType,
        legendText: populateLegendText(isConcentrationView, section, notEnrolledType)
      }
    )
  } else if (enrolledValue === 1) {
    legends.push({
      type: enrolledType,
      legendText: populateLegendText(isConcentrationView, section, enrolledType)
    })
  } else if (enrolledValue === 0) {
    legends.push({
      type: notEnrolledType,
      legendText: populateLegendText(isConcentrationView, section, notEnrolledType)
    })
  }

  return legends
}

export const getEnrollmentData = (aggregationData) => {
  let enrolledCount = 0
  let notEnrolledCount = 0
  if (aggregationData && aggregationData.length > 0) {
    enrolledCount =
      aggregationData?.reduce((acc, group) => {
        return acc + group.enrolled
      }, 0) || 0
    notEnrolledCount =
      aggregationData?.reduce((acc, group) => {
        return acc + group.notEnrolled
      }, 0) || 0
  }
  return {
    enrolledCount,
    notEnrolledCount
  }
}

export const getOutreachSimulationTooltipFieldData = (data = null) => {
  return {
    medicaidEligIndCount: data?.medicaidEligIndCount,
    medicaidEligIndNotEnrolled: data?.medicaidEligIndNotEnrolled,
    medicareEligibleCount: data?.medicareEligibleCount,
    medicareEligibleNotEnrolled: data?.medicareEligibleNotEnrolled,
    usdaSnapIndCount: data?.usdaSnapIndCount,
    usdaSnapIndNotEnrolled: data?.usdaSnapIndNotEnrolled,
    usdaSnapIndEnrolled: (data?.usdaSnapIndCount || 0) - (data?.usdaSnapIndNotEnrolled || 0),
    electricianRecruitmentEligibleCount: data?.electricianRecruitmentEligibleCount,
    electricianRecruitmentEligibleNotEnrolled: data?.electricianRecruitmentEligibleNotEnrolled
  }
}

export const getOutreachSimulationTooltipFields = () => {
  return Object.keys(getOutreachSimulationTooltipFieldData())
}

const outreachSimulationFieldMappings = {
  SNAP: {
    eligible: 'usdaSnapIndCount',
    notEnrolled: 'usdaSnapIndNotEnrolled',
    enrolled: 'usdaSnapIndEnrolled'
  },
  Medicaid: {
    eligible: 'medicaidEligIndCount',
    notEnrolled: 'medicaidEligIndNotEnrolled'
  },
  Medicare: {
    eligible: 'medicareEligibleCount',
    notEnrolled: 'medicareEligibleNotEnrolled'
  },
  'Electricians and Installers': {
    eligible: 'electricianRecruitmentEligibleCount',
    notEnrolled: 'electricianRecruitmentEligibleNotEnrolled'
  }
}

/**
 * Get household tooltip stats using new filter management data
 * @param {string} activeOutreachType - 'benefits' | 'workforce' | null (from useOutreachFilters)
 * @param {Array} selectedOptionsWithConfig - Array of selected options with enrolledKey (from useOutreachFilters)
 * @param {Object} data - Household data containing enrollment values
 * @returns {Object} Tooltip info object
 */
export const getOutreachHouseholdTooltipStats = (activeOutreachType, selectedOptionsWithConfig, data) => {
  if (!activeOutreachType || !selectedOptionsWithConfig?.length) {
    return {}
  }

  const isBenefitsEligibility = activeOutreachType === 'benefits'
  const eligibleValues = selectedOptionsWithConfig.map((option) => option.label)
  const enrolledValues = []

  for (const option of selectedOptionsWithConfig) {
    if (data[option.enrolledKey] !== null && data[option.enrolledKey] === 1) {
      enrolledValues.push(option.label)
    }
  }

  let toolTipInfo
  if (isBenefitsEligibility) {
    toolTipInfo = {
      section: 'Benefits Eligibility',
      eligibleLabel: 'Eligible Benefits',
      enrolledLabel: 'Enrolled Benefits'
    }
  } else {
    toolTipInfo = {
      section: 'Eligible Candidate',
      eligibleLabel: 'Eligibility',
      enrolledLabel: 'Enrolled'
    }
  }

  return {
    ...toolTipInfo,
    eligibleValue: eligibleValues.join(', ') || 'None from Selected',
    enrolledValue: enrolledValues.join(', ') || 'None from Selected'
  }
}

/**
 * Get tile layer tooltip stats using new filter management data
 * @param {string} activeOutreachType - 'benefits' | 'workforce' | null (from useOutreachFilters)
 * @param {Array} selectedOptionsWithConfig - Array of selected options with config (from useOutreachFilters)
 * @returns {Object} Tooltip info object with section, sectionKey, and data array
 */
export const getOutreachTileLayerTooltipStats = (activeOutreachType, selectedOptionsWithConfig) => {
  if (!activeOutreachType || !selectedOptionsWithConfig?.length) {
    return {}
  }

  const isBenefitsEligibility = activeOutreachType === 'benefits'

  return {
    section: isBenefitsEligibility ? 'Benefits Eligibility' : 'Workforce Recruitment',
    sectionKey: isBenefitsEligibility ? 'Benefits' : 'Recruitment',
    data: selectedOptionsWithConfig.map((option) => ({
      key: option.label,
      name: isBenefitsEligibility ? `${option.label} Population` : option.label,
      ...outreachSimulationFieldMappings[option.label]
    }))
  }
}

export const getPOIImageUrl = (imageName) => {
  if (imageName && typeof imageName === 'string') {
    try {
      return require(`../../icons/map/poi/${imageName}`)
    } catch {
      return null
    }
  }
  return null
}

export const handleRetainRecentSearches = () => {
  const recentSearches = localStorage.getItem(RECENT_SEARCHES_KEY)
  globalThis.localStorage.clear()
  if (recentSearches) {
    const parsedSearches = JSON.parse(recentSearches)
    globalThis.localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(parsedSearches))
  }
}

// CUSTOMIZED LABEL CHANGES
export const getUseCaseCustomLabel = (useCaseList, label, section) => {
  if (!useCaseList?.useCaseList) return label

  const arr = useCaseList.useCaseList[section]
  if (!arr) return label

  const item = arr?.find((obj) => obj.name === label)
  if (!item) return label

  return isValidCustomString(item.custom_name) ? item.custom_name : label
}

export const getPersonaCustomLabel = (personaList, title, name, type) => {
  if (
    !personaList?.personaList ||
    personaList?.personaList?.data === null ||
    personaList?.personaList?.data?.length === 0 ||
    personaList?.length === 0
  )
    return name === null ? title : name

  if (name === null) {
    const found = personaList?.personaList?.data?.find((item) => item.type === type)
    return isValidCustomString(found?.custom_title) ? found.custom_title : title
  } else {
    const found = personaList?.personaList?.data?.find((item) => item.type === type)
    if (!found) return name
    const nameObj = found.data.find((e) => e.name === name)
    if (nameObj) {
      return isValidCustomString(nameObj.custom_name) ? nameObj.custom_name : name
    } else {
      return name
    }
  }
}

export const getHealthAndEquityCustomLabel = (healthAndEquity, name, key) => {
  if (!healthAndEquity?.healthAndEquity) return name

  if (key === 'custom_title') {
    if (
      (healthAndEquity?.healthAndEquity?.title === name ||
        healthAndEquity?.healthAndEquity?.title === 'Health and Equity Condition(s)') &&
      isValidCustomString(healthAndEquity?.healthAndEquity?.custom_title)
    ) {
      return healthAndEquity?.healthAndEquity?.custom_title
    }
    return name
  }
  return name
}

export const getSdohCustomLabel = (sdohFilterCategory, name, key) => {
  if (key === 'custom_title') {
    if (
      sdohFilterCategory?.sdohFilterCategory?.title === name ||
      sdohFilterCategory?.sdohFilterCategory?.title === 'Social Drivers of Health'
    ) {
      const customTitle = sdohFilterCategory?.sdohFilterCategory?.custom_title
      return isValidCustomString(customTitle) ? customTitle : name
    }
    return name
  }

  if (key === 'FILTER_CATEGORY_CUSTOM_NAME') {
    const category = sdohFilterCategory?.sdohFilterCategory?.filter_category_name?.find(
      (c) => c.FILTER_CATEGORY_NAME === name
    )
    if (category) {
      const customCategoryName = category?.FILTER_CATEGORY_CUSTOM_NAME
      return isValidCustomString(customCategoryName) ? customCategoryName : name
    }
    return name
  }
  return name
}

export const getPersonaCustomName = (persona, personasConfig) => {
  const updatedName = personasConfig?.customPersonaNames?.find((name) => name?.persona === persona)?.customName
  return isValidCustomString(updatedName) ? updatedName : persona
}

export const isValidCustomString = (value) => {
  return typeof value === 'string' && value.trim() !== ''
}

/* Function Descriptions:
  NEW FILTER MANAGEMENT SYSTEM:
  - getOutreachLegends: Generates legends using new filter data (activeOutreachType, enrolledValue).
  - getOutreachHouseholdTooltipStats: Generates household tooltip stats using new filter data.
  - getOutreachTileLayerTooltipStats: Generates tile layer tooltip stats using new filter data.

  UTILITY FUNCTIONS:
  - isNotEmpty: Checks if the provided data is a non-empty array.
  - enrolledOptions: Array of enrolled option values (All/Yes/No).
  - getEnrollmentData: Aggregates and returns the total enrolled and not enrolled counts from the data.
  - getOutreachSimulationTooltipFieldData: Extracts specific tooltip field data from the provided data object.
  - getOutreachSimulationTooltipFields: Returns the keys of the outreach simulation tooltip field data.
  - outreachSimulationFieldMappings: Maps option labels to their data field names.
  - handleRetainRecentSearches: Clears localstorage during logout after taking a copy of recent searches.
  - getPOIImageUrl: Returns the image URL for a given point of interest (POI) image name.
  - getUseCaseCustomLabel: Retrieves the custom label for a use case based on the provided section and label.
  - getPersonaCustomLabel: Retrieves the custom label for a persona based on the provided title, name, and type.
  - getHealthAndEquityCustomLabel: Retrieves the custom label for health and equity based on the provided name and key.
  - getSdohCustomLabel: Retrieves the custom label for SDOH based on the provided name and key.
  - isValidCustomString: Checks if a given string is valid (not empty or whitespace).
  */
